import React, { useState } from "react";
import { X, Lock, Eye, EyeOff } from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import "../styles/ChangePasswordModal.css";

const ChangePasswordModal = ({ onClose }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError("הסיסמאות החדשות אינן תואמות");
      return;
    }

    // Validate password length
    if (formData.newPassword.length < 6) {
      setError("הסיסמה החדשה חייבת להכיל לפחות 6 תווים");
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.changePassword(
        user.username,
        formData.currentPassword,
        formData.newPassword
      );

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(response.error || "שגיאה בשינוי הסיסמה");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setError("הסיסמה הנוכחית שגויה");
      } else {
        setError(error.response?.data?.error || "שגיאה בשינוי הסיסמה");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='password-modal-overlay' onClick={onClose}>
      <div className='password-modal' onClick={(e) => e.stopPropagation()}>
        <div className='password-modal-header'>
          <div className='header-title'>
            <Lock size={24} />
            <h2>שינוי סיסמה</h2>
          </div>
          <button className='close-btn' onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className='password-success'>
            <div className='success-icon'>✓</div>
            <p>הסיסמה שונתה בהצלחה!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className='password-form'>
            {error && <div className='password-error'>{error}</div>}

            <div className='password-field'>
              <label>סיסמה נוכחית</label>
              <div className='password-input-wrapper'>
                <input
                  type={showPasswords.current ? "text" : "password"}
                  name='currentPassword'
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                  placeholder='הזן את הסיסמה הנוכחית'
                />
                <button
                  type='button'
                  className='toggle-password'
                  onClick={() => togglePasswordVisibility("current")}
                >
                  {showPasswords.current ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            <div className='password-field'>
              <label>סיסמה חדשה</label>
              <div className='password-input-wrapper'>
                <input
                  type={showPasswords.new ? "text" : "password"}
                  name='newPassword'
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                  minLength={6}
                  placeholder='הזן סיסמה חדשה (מינימום 6 תווים)'
                />
                <button
                  type='button'
                  className='toggle-password'
                  onClick={() => togglePasswordVisibility("new")}
                >
                  {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className='password-field'>
              <label>אימות סיסמה חדשה</label>
              <div className='password-input-wrapper'>
                <input
                  type={showPasswords.confirm ? "text" : "password"}
                  name='confirmPassword'
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder='הזן שוב את הסיסמה החדשה'
                />
                <button
                  type='button'
                  className='toggle-password'
                  onClick={() => togglePasswordVisibility("confirm")}
                >
                  {showPasswords.confirm ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            <div className='password-actions'>
              <button
                type='button'
                className='btn-cancel'
                onClick={onClose}
                disabled={loading}
              >
                ביטול
              </button>
              <button type='submit' className='btn-submit' disabled={loading}>
                {loading ? "משנה..." : "שנה סיסמה"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
